import type { ServerSideSelectionState } from '@civilgrid/ui/components';
import { createEmptyFilterGroup } from '@civilgrid/ui/hooks';
import { DEFAULT_PAGE_SIZE, type FilterGroupItem } from '@civilgrid/ui/utils';
import type { PaginationState, SortingState } from '@tanstack/react-table';
import type { MultiPolygon } from 'geojson';
import { capitalize, mergeWith, toArray } from 'lodash';
import set from 'lodash/set';
import { DateTime } from 'luxon';
import { proxy } from 'valtio';
import { geomToMultiPolygon } from '~/common/geoUtils';
import { compareWithArrayCopy } from '~/common/storeUtils';
import {
  defaultBasemapStyle,
  EditorMode,
  type MapDetail,
  type MapObject,
  type Project,
  type ProjectGeometryType,
  type TLoadedState,
  type TLoadingState,
  type UnitSystem,
} from '~/common/types';
import { MapStoreBase } from '~/components2/Map';
import { CIRCLE_PROJECT_RADIUS } from '~/pages/common/TopNav/common';

// --- Map store (home map + new project drawing) ---

export type PopupParams = {
  lngLat: [number, number];
  id: string;
  projectId: string;
  projectName: string;
  projectManager: string;
  projectPhase: string;
  projectYear: string;
  projectBillingTier: Project['billingTier'];
  projectAddress: string;
} & ({ isCrossOrgProject?: false } | { isCrossOrgProject: true; organizationName: string });

class HomeMapStore extends MapStoreBase {
  inNewProjectMode = false;
  inUploadKMZMode = false;
  isGeometryFromKMZ = false;
  kmzFileName: string | undefined = undefined;

  // Track if initial fitBounds on home map has been done (to prevent re-zoom on basemap change)
  hasInitialFitBounds = false;

  editorMode: EditorMode = EditorMode.None;
  currentMap: TLoadingState | (MapDetail & TLoadedState) = {
    isLoading: false,
    id: 'temp',
    name: 'temp',
    image: 'temp',
    unitSystem: 'imperial',
    baseMap: defaultBasemapStyle,
    createdAt: DateTime.now(),
    createdBy: {
      name: 'temp',
    },
    updatedAt: DateTime.now(),
    updatedBy: {
      name: 'temp',
    },
    objects: {},
  };
  addressSearchResult:
    | {
        lngLat: [number, number];
        address: string;
      }
    | undefined = undefined;

  geometryType: ProjectGeometryType | undefined;

  projectArea: number = 0;
  projectAreaLimit: number = 0;

  popupParams?: PopupParams = undefined;
  isPopupDisabled: boolean = false;
  lineBuffer: number = 40;
  objectNameCounter: number = 0;

  hoverProjectId?: string = undefined;

  /** When set, Map will fit bounds to this project and show its popup, then clear. */
  flyToProject: Project | null = null;

  get currentObject() {
    if (!this.currentMap.isLoading && this.currentObjectId) {
      return this.currentMap.objects[this.currentObjectId];
    }
    return undefined;
  }

  get objectsAsArray(): MapObject[] {
    if (!this.currentMap.isLoading) {
      return toArray(this.currentMap.objects);
    } else {
      return [];
    }
  }

  get geometryAsMultiPolygon(): MultiPolygon | undefined {
    const objectsAsArray = this.objectsAsArray;
    if (objectsAsArray.length) {
      return geomToMultiPolygon({
        objectsAsArray,
        geometryType: this.geometryType,
      });
    }
    return undefined;
  }

  setHasInitialFitBounds(value: boolean) {
    this.hasInitialFitBounds = value;
  }

  setNewProjectMode(newProjectMode: boolean) {
    this.inNewProjectMode = newProjectMode;

    if (!newProjectMode) {
      this.resetObjects();
      this.setEditorMode(EditorMode.None);
      this.objectNameCounter = 0;
      this.lineBuffer = 40;
    }
  }

  setInUploadKMZMode(inUploadKMZMode: boolean) {
    this.inUploadKMZMode = inUploadKMZMode;
  }

  setIsGeometryFromKMZ(isGeometryFromKMZ: boolean) {
    this.isGeometryFromKMZ = isGeometryFromKMZ;
  }

  setKmzFileName(kmzFileName: string | undefined) {
    this.kmzFileName = kmzFileName;
  }

  resetObjects() {
    if (!this.currentMap.isLoading) {
      this.currentMap.objects = {};
    }
  }

  get objectsOnMainSurface(): { [key: string]: MapObject } {
    if (!this.currentMap.isLoading) {
      if (this.currentObjectId) {
        const copy = {
          ...this.currentMap.objects,
        };
        delete copy[this.currentObjectId];
        return copy;
      } else {
        return this.currentMap.objects;
      }
    } else {
      return {};
    }
  }

  get currentUnitSystem(): UnitSystem {
    return 'imperial';
  }

  setEditorMode(mode: EditorMode): void {
    if (mode === EditorMode.None) {
      this.currentObjectId = undefined;
    }
    this.editorMode = mode;
  }

  setGeometryType(type: ProjectGeometryType) {
    this.geometryType = type;
  }

  generateSortOrderKey(): string {
    return 'abc';
  }

  setCurrentObjectId(objectId: string): void {
    if (!this.currentMap.isLoading) {
      const object = this.currentMap.objects[objectId];
      if (object) {
        this.currentObjectId = object.id;
        if (object.type === 'polygon') {
          this.setEditorMode(EditorMode.EditPolygon);
        } else if (object.type === 'line') {
          this.setEditorMode(EditorMode.EditLine);
        } else if (object.type === 'circle') {
          this.setEditorMode(EditorMode.EditCircle);
        }
      }
    }
  }
  resetCurrentObjectId(): void {
    this.currentObjectId = undefined;
    this.setEditorMode(EditorMode.None);
  }

  async newObject(
    o: Omit<MapObject, 'createdBy' | 'updatedBy'>,
    setCurrentObject?: boolean | undefined,
    map?: mapboxgl.Map | undefined,
  ): Promise<MapObject | undefined> {
    if (o.type === 'circle') {
      o.properties = {
        ...o.properties,
        radius: CIRCLE_PROJECT_RADIUS,
        useMilesToDisplayRadius: true,
      };
    }

    o.title = `${capitalize(o.type)} ${++this.objectNameCounter}`;
    o.properties = {
      ...o.properties,
      color: 'orange',
    };

    if (o.type === 'line') {
      o.properties = {
        ...o.properties,
        buffer: this.lineBuffer,
      };
    }

    set(this.currentMap, `objects.${o.id}`, o);
    this.setCurrentObjectId(o.id);
    return undefined;
  }

  async updateObject<T extends MapObject>(o: Partial<T>): Promise<void> {
    if (!this.currentMap.isLoading) {
      if (o.id) {
        const merged = mergeWith(this.currentMap.objects[o.id], o, compareWithArrayCopy);
        this.currentMap.objects[o.id] = merged;
      } else if (this.currentObjectId) {
        const merged = mergeWith(this.currentObject, o, compareWithArrayCopy);
        this.currentMap.objects[this.currentObjectId!] = merged;
      }
    }
  }

  async deleteObject<T = MapObject>(o: T, shouldPushToUndoStack?: boolean): Promise<MapObject | undefined> {
    if (!this.currentMap.isLoading) {
      delete this.currentMap.objects[(o as MapObject).id];
    }
    return undefined;
  }

  setAddressSearchResult(result?: { lngLat: [number, number]; address: string }) {
    this.addressSearchResult = result;
  }

  setPopupParams(popupParams: PopupParams | undefined) {
    this.popupParams = popupParams;
  }

  setHoverProjectId(projectId: string | undefined) {
    this.hoverProjectId = projectId;
  }

  setFlyToProject(project: Project | null) {
    this.flyToProject = project;
  }

  updateLineBuffer(buffer: number) {
    this.lineBuffer = buffer;
  }
}

export const homeMapStore = proxy(new HomeMapStore());

// --- Table / list view state ---

const defaultTableSelection: ServerSideSelectionState = {
  mode: 'partial',
  selectedIds: [],
};

/**
 * Valtio store scoped to homeViewV2. Holds shared table state (filters, sort, pagination)
 * and header state (count/loading from table and map list). RootStore is only used for
 * currentOrganizationId and inOnboardingMode.
 */
export const homeViewStore = proxy({
  // Header state (written by ProjectsTable / ProjectsMapList, read by index)
  tableCount: 0,
  mapCount: 0,
  tableLoading: false,
  mapLoading: false,

  // Shared table state for Split and Table views (and Map list uses filters + globalFilter)
  pagination: {
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  } as PaginationState,
  sorting: [] as SortingState,
  filters: createEmptyFilterGroup(),
  globalFilter: '',
  /** Bookmarks-only view (Table/Split). Passed to API as bookmarkedOnly, not a filter. */
  bookmarksOnly: false,

  /** Table row selection (persisted so it survives switching to Map and back). */
  tableSelection: defaultTableSelection as ServerSideSelectionState,

  /** When set, filter table to projects intersecting this map viewport geometry (Filter Table to Map View). */
  mapGeometryFilter: null as MultiPolygon | null,
});

export function setTableCount(value: number) {
  homeViewStore.tableCount = value;
}

export function setMapCount(value: number) {
  homeViewStore.mapCount = value;
}

export function setTableLoading(value: boolean) {
  homeViewStore.tableLoading = value;
}

export function setMapLoading(value: boolean) {
  homeViewStore.mapLoading = value;
}

export function setPagination(updaterOrValue: PaginationState | ((prev: PaginationState) => PaginationState)) {
  homeViewStore.pagination =
    typeof updaterOrValue === 'function' ? updaterOrValue(homeViewStore.pagination) : updaterOrValue;
}

export function setSorting(updaterOrValue: SortingState | ((prev: SortingState) => SortingState)) {
  homeViewStore.sorting = typeof updaterOrValue === 'function' ? updaterOrValue(homeViewStore.sorting) : updaterOrValue;
}

export function setFilters(fg: FilterGroupItem) {
  homeViewStore.filters = fg;
  homeViewStore.pagination = { ...homeViewStore.pagination, pageIndex: 0 };
}

export function setGlobalFilter(value: string) {
  homeViewStore.globalFilter = value;
  homeViewStore.pagination = { ...homeViewStore.pagination, pageIndex: 0 };
}

export function setBookmarksOnly(value: boolean) {
  homeViewStore.bookmarksOnly = value;
  homeViewStore.pagination = { ...homeViewStore.pagination, pageIndex: 0 };
}

export function setMapGeometryFilter(value: MultiPolygon | null) {
  homeViewStore.mapGeometryFilter = value;
  homeViewStore.pagination = { ...homeViewStore.pagination, pageIndex: 0 };
}

export function setPageSize(size: number) {
  homeViewStore.pagination = {
    ...homeViewStore.pagination,
    pageSize: size,
    pageIndex: 0,
  };
}

export function setCurrentPage(page: number) {
  homeViewStore.pagination = {
    ...homeViewStore.pagination,
    pageIndex: page - 1,
  };
}

export function resetFilters() {
  homeViewStore.filters = createEmptyFilterGroup();
  homeViewStore.pagination = { ...homeViewStore.pagination, pageIndex: 0 };
}

export function setTableSelection(state: ServerSideSelectionState) {
  homeViewStore.tableSelection = state;
}
